import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import * as API from '../../../constants/api';
import client from '../../../services/template-api';
import cycleClient from '../../../services/cycle-api';

import { useFormState } from '../../../hooks/useFormState';
import { useFetchOptionsForPaginatedSelectWithSWR } from '../../../hooks/useFetchOptionsForPaginatedSelectWithSWR';
import { useSwrPageRequestKeysManagingWithContextSaving } from '../../../hooks/useSwrPageRequestKeysManagingWithContextSaving';
import { useSwrRequestKeysToMutateFromContext } from '../../../hooks/useSwrRequestKeysToMutateFromContext';

import { mutateListDataAfterCreatingItem } from '../../../helpers/swrMutateUtils';
import { mergeTwoArraysRemovingDuplicates } from '../../../helpers/mergeTwoArraysRemovingDuplicates';

import { SORT_TYPES } from '../../../constants/sort';
import { defaultErrorMessage } from '../../../constants/errorMessages';

import Modal from '../../../design-system/Modal/Modal';
import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import ModalHeader from '../../../design-system/ModalHeader/ModalHeader';
import PaginatedSelect from '../../../design-system/PaginatedSelect/PaginatedSelect';
import { Button, Input, TextArea } from '../../../design-system';

const CreateCycleModal = ({ currentKey, requestKeysToMutate: _requestKeysToMutate, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { formData, handleInputChange } = useFormState({
        name: '',
        description: '',
        template: null,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const {
        requestKeysToMutate: { cycles: requestKeysToMutateFromContext },
    } = useSwrRequestKeysToMutateFromContext();

    const {
        options: templateOptions,
        optionsLoading,
        totalOptions,
        setPage,
        canLoadMoreOptions,
        key,
    } = useFetchOptionsForPaginatedSelectWithSWR({
        client,
        route: API.ROUTES.template.playbookTemplate,
        searchParams: { ordering: SORT_TYPES.created },
        formatResponseToOptions: (results) => results?.map(({ id, name }) => ({ id, name })),
    });

    useSwrPageRequestKeysManagingWithContextSaving({ pageAccessor: 'playbookTemplates', key });

    const allRequiredFieldsFilled = !!formData.name?.trim() && !!formData.description;
    const submitButtonState = isLoading
        ? 'loading'
        : allRequiredFieldsFilled
        ? 'default'
        : 'disabled';

    const handleSubmit = async () => {
        if (!allRequiredFieldsFilled) return;

        try {
            setIsLoading(true);

            const { data } = await cycleClient.post(API.ROUTES.cycle.cycle, formData);

            const requestKeysToMutate = mergeTwoArraysRemovingDuplicates(
                _requestKeysToMutate,
                requestKeysToMutateFromContext
            );
            void mutateListDataAfterCreatingItem({ requestKeysToMutate, currentKey });

            const cycleId = data.id;
            navigate(`/configure/cycle/${cycleId}`, { state: { from: location } });

            onClose();
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setIsLoading(false);
        }
    };

    const increaseComponentHeightBy = totalOptions < 3 ? 68 : 116;

    return (
        <Modal onClose={onClose} size="medium" resetPadding>
            <ModalHeader title="New Cycle" outlined onClose={onClose} />
            <div className="py-5 px-5 sm:px-8 flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                    <Input
                        size="xs"
                        name="cycle_name"
                        value={formData.name}
                        label="Cycle Name"
                        isRequired
                        placeholder="Give this cycle a clear, unique name for the solution"
                        onChange={(e) => handleInputChange('name', e.target.value)}
                    />

                    <TextArea
                        name="cycle_description"
                        value={formData.description}
                        label="Cycle Description"
                        isRequired
                        placeholder="Explain what this solution does"
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        height="160px"
                    />

                    <PaginatedSelect
                        size="xs"
                        name="template"
                        value={formData.template}
                        options={templateOptions}
                        onChange={(template) => handleInputChange('template', template)}
                        label="Select Template (optional)"
                        placeholder="Select a playbook template to connect"
                        fetchOptions={() => setPage((page) => page + 1)}
                        canLoadMore={canLoadMoreOptions}
                        optionsLoading={optionsLoading}
                        includeClientSideFiltering
                        totalOptionsCount={totalOptions}
                        dropdownHeight={184}
                        increaseComponentHeightBy={increaseComponentHeightBy}
                    />
                </div>

                <div className="flex items-center justify-between gap-4">
                    <Button type="neutral" size="md" text="Cancel" onClick={onClose} />
                    <Button
                        type="secondary"
                        size="md"
                        text="Create"
                        state={submitButtonState}
                        onClick={handleSubmit}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default CreateCycleModal;
